import Button from '2-core-elements/Buttons/Button';
import SelectCustom, { type SelectCustomOption } from '5-react-form-apps/FormElements/SelectCustom';
import FormFieldWrapper from '6-my-mcc/FormElements/FormFieldWrapper/FormFieldWrapper';
import { useCallback, useEffect, useMemo, type FC } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import useSupportTeamsMutation from '../../Utils/useSupportTeamsMutation';
import LoadingIndicator from '3-components/LoadingIndicator/LoadingIndicator';
import { type SupportTeam } from '../../Utils/useSupportTeamsQuery';
import useProfileQuery, { type ProfileDetail } from '../../Utils/useProfileQuery';
import { pushGTMEvent } from 'utils/eventGTM';

type FormValue = { list: SelectCustomOption[]; defaultValue?: SupportTeam } | null;
type SupportTeamType = 'afl' | 'bbl';
type FormData = {
    aflTeams: SupportTeam;
    bblTeams: SupportTeam;
}

interface FormMyTeamsProps {
    profileDetail: Omit<ProfileDetail, 'specialInterests'>;
    supportTeams: {
        aflTeams: SupportTeam[];
        bblTeams: SupportTeam[];
    };
    onSubmitSuccess: () => void;
}

const FormMyTeams: FC<FormMyTeamsProps> = ({ profileDetail, supportTeams, onSubmitSuccess }) => {
    const methods = useForm<FormData>();
    const { control, handleSubmit, setValue, formState: { errors } } = methods;
    const submitFormMutation = useSupportTeamsMutation();
    const { refetch: profileRefetch, isFetching: isProfileRefetch } = useProfileQuery();

    useEffect(() => {
        pushGTMEvent({
            actionType: 'form_start',
            name: 'My teams',
            type: 'Profile',
        });
    }, []);

    useEffect(() => {
        if (errors.aflTeams || errors.bblTeams) {
            pushGTMEvent({
                actionType: 'form_error',
                name: 'My teams',
                type: 'Profile',
                step_name: 'My teams',
                step_number: 1,
                total_steps: 1,
                message: JSON.stringify(errors),
            });
        }
    }, [errors]);

    const formValueGenerator = useCallback((type: SupportTeamType): FormValue => {
        if (!supportTeams.aflTeams || !supportTeams.bblTeams) {
            return null;
        }

        const selectedList = type === 'afl' ? supportTeams.aflTeams : supportTeams.bblTeams;
        const list: SelectCustomOption[] = selectedList.map(item => {
            return {
                label: item.name || '',
                value: item.displayName || '',
            };
        });

        const selectedSupportType = type === 'afl' ? profileDetail.aflTeamOfSupport : profileDetail.bblTeamOfSupport;
        const defaultValue = selectedList.find(item => {
            return item.name === selectedSupportType;
        });

        return { list, defaultValue };
    }, [profileDetail.aflTeamOfSupport, profileDetail.bblTeamOfSupport, supportTeams.aflTeams, supportTeams.bblTeams]);

    const aflTeams = useMemo((): FormValue => {
        if (supportTeams.aflTeams) {
            return formValueGenerator('afl');
        }
        return null;
    }, [supportTeams.aflTeams, formValueGenerator]);

    const bblTeams = useMemo((): FormValue => {
        if (supportTeams.bblTeams) {
            return formValueGenerator('bbl');
        }
        return null;
    }, [supportTeams.bblTeams, formValueGenerator]);

    const findSupportTeam = useCallback((displayName: string, type: SupportTeamType): SupportTeam | undefined => {
        return (type === 'afl' ? supportTeams.aflTeams : supportTeams.bblTeams).find(item => {
            return item.displayName === displayName;
        });
    }, [supportTeams.aflTeams, supportTeams.bblTeams]);

    const onSubmit = (data: FormData): void => {
        submitFormMutation.mutate({
            aflTeamOfSupport: data.aflTeams?.displayName || null,
            bblTeamOfSupport: data.bblTeams?.displayName || null,
        }, {
            onSuccess: async() => {
                await profileRefetch();
                onSubmitSuccess();

                pushGTMEvent({
                    actionType: 'form_complete',
                    name: 'My teams',
                    type: 'Profile',
                });
            },
        });
    };

    return (
        <div className="mcc-form-wrapper">
            {/* eslint-disable-next-line react/jsx-props-no-spreading */}
            <FormProvider {...methods}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="form-my-teams-wrapper">
                        <FormFieldWrapper
                            id="my-teams-afl"
                            label="AFL Team"
                            isOptional={true}
                        >
                            <Controller
                                control={control}
                                name="aflTeams"
                                defaultValue={aflTeams?.defaultValue}
                                render={({ field }): JSX.Element => {
                                    return (
                                        <SelectCustom
                                            placeholder="No Selection"
                                            id="form-my-teams-afl"
                                            canUnsetValue={true}
                                            options={aflTeams?.list || []}
                                            onChange={(value): void => {
                                                if (value?.value && typeof value.value === 'string') {
                                                    const supportTeam = findSupportTeam(value.value, 'afl');
                                                    if (supportTeam) {
                                                        setValue('aflTeams', supportTeam);
                                                    }
                                                }
                                            }}
                                            value={aflTeams?.defaultValue ? {
                                                label: field.value?.name || '',
                                                value: field.value?.displayName || '',
                                            } : null}
                                        />
                                    );
                                }}
                            />
                        </FormFieldWrapper>
                        <FormFieldWrapper
                            id="my-teams-bbl"
                            label="BBL Team"
                            isOptional={true}
                        >
                            <Controller
                                control={control}
                                name="bblTeams"
                                defaultValue={bblTeams?.defaultValue}
                                render={({ field }): JSX.Element => {
                                    return (
                                        <SelectCustom
                                            placeholder="No Selection"
                                            id="form-my-teams-bbl"
                                            canUnsetValue={true}
                                            options={bblTeams?.list || []}
                                            onChange={(value): void => {
                                                if (value && typeof value.value === 'string') {
                                                    const supportTeam = findSupportTeam(value.value, 'bbl');
                                                    if (supportTeam) {
                                                        setValue('bblTeams', supportTeam);
                                                    }
                                                }
                                            }}
                                            value={bblTeams?.defaultValue ? {
                                                label: field.value?.name || '',
                                                value: field.value?.displayName || '',
                                            } : null}
                                        />
                                    );
                                }}
                            />
                        </FormFieldWrapper>
                    </div>

                    {submitFormMutation.isLoading || isProfileRefetch ? (
                        <LoadingIndicator />
                    ) : (
                        <Button type="submit" fullWidth={true}>Save</Button>
                    )}
                </form>
            </FormProvider>
        </div>
    );
};

export default FormMyTeams;
